// Offscreen render at exactly outW x outH from the full-resolution images. Same scene graph
// as the on-screen stage, scale 1 — so what the customer saw is what gets printed.

import Konva from 'konva';
import type { ImageObject, TextObject } from './design';
import type { Geometry } from './geometry';
import type { LoadedImage } from './image';
import { centreNode, loadFonts, textConfig } from './text';

export function clipCircle(g: Geometry) {
  return (ctx: Konva.Context) => {
    ctx.beginPath();
    ctx.arc(g.outW / 2, g.outH / 2, (g.trimWidthMm / 2 + g.bleedMm) * g.pxPerMm, 0, Math.PI * 2, false);
    ctx.closePath();
  };
}

export async function renderPng(g: Geometry, images: ImageObject[], loaded: Record<string, LoadedImage>, texts: TextObject[]): Promise<Blob> {
  await loadFonts([...new Set(texts.map((t) => t.font))]);
  const container = document.createElement('div');
  const stage = new Konva.Stage({ container, width: g.outW, height: g.outH });
  const layer = new Konva.Layer(g.shape === 'circle' ? { clipFunc: clipCircle(g) } : {});
  for (const im of images) {
    const src = loaded[im.id];
    if (!src) continue;
    layer.add(new Konva.Image({
      image: src.full as CanvasImageSource, x: im.centerX, y: im.centerY,
      offsetX: src.width / 2, offsetY: src.height / 2, scaleX: im.scale, scaleY: im.scale, rotation: im.rotation,
    }));
  }
  for (const t of texts) {
    const node = new Konva.Text(textConfig(t, 1));
    centreNode(node);
    layer.add(node);
  }
  stage.add(layer);
  layer.draw();
  try {
    const canvas = stage.toCanvas({ pixelRatio: 1 });
    if (canvas.width !== g.outW || canvas.height !== g.outH) throw new Error(`export is ${canvas.width}x${canvas.height}`);
    return await new Promise<Blob>((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/png'));
  } finally {
    stage.destroy();
  }
}
